import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import DataTable from "../../../components/DataTable";
import { fetchAgentClients } from "../../../controller/ibDashboard/clientsController";
import { fetchAgentClientAccounts } from "../../../controller/ibDashboard/getClientsController";
import { formatIbClientName } from "../../../utils/ibClientDisplayName";
import { extractEmailLoose } from "../../../utils/extractEmailLoose";
import AppConstants from "../../../utils/appConstants";
import { useMediaMaxWidth } from "../../../hooks/useMediaMaxWidth";
import { getPermissionControllers, hasBrokerPermission, isAccountPermissionDisabled, isBroker } from "../../../utils/permissionHelper";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import "../../css/List.css";
import "../../css/IbDashboard.css";

const PER_PAGE = 8;
const NAN = "NAN";

const isMissing = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && String(v).trim() === "") ||
  (typeof v === "number" && Number.isNaN(v));

const disp = (v) => (isMissing(v) ? NAN : typeof v === "string" ? v.trim() : String(v));
const normalizeSearchValue = (v) => String(v || "").toLowerCase().replace(/\s+/g, "").trim();

const firstDefined = (...vals) => {
  for (const i of vals) {
    if (!isMissing(i)) return i;
  }
  return null;
};

const getUserInfo = (c) => c?.user_information || c?.userInformation || {};

function inferUserType(rawNode) {
  const info = getUserInfo(rawNode);
  const rawType = firstDefined(
    rawNode?.USER_TYPE,
    rawNode?.user_type,
    rawNode?.TYPE,
    rawNode?.type,
    rawNode?.ROLE,
    rawNode?.role,
    rawNode?.ACCOUNT_TYPE,
    rawNode?.account_type,
    info?.USER_TYPE,
    info?.user_type,
    info?.TYPE,
    info?.type,
    info?.ROLE,
    info?.role
  );

  const normalized = String(rawType ?? "").trim().toLowerCase();
  if (!normalized) return "unknown";
  if (["broker", "ib", "sub_broker", "subbroker"].includes(normalized)) return "broker";
  if (["client", "customer", "user", "trader"].includes(normalized)) return "client";

  // Numeric fallback (common backend patterns).
  const asNum = Number(normalized);
  if (Number.isFinite(asNum)) {
    if (asNum === 1) return "broker";
    if (asNum === 0 || asNum === 2) return "client";
  }
  return normalized;
}

function extractClientsArray(response) {
  let data = [];
  if (!response) return data;
  if (Array.isArray(response.DATA)) data = response.DATA;
  else if (response.DATA && Array.isArray(response.DATA.clients)) data = response.DATA.clients;
  else if (response.DATA && Array.isArray(response.DATA.data)) data = response.DATA.data;
  else if (Array.isArray(response)) data = response;
  else if (Array.isArray(response.clients)) data = response.clients;
  else if (Array.isArray(response.data)) data = response.data;
  else if (response.DATA && !Array.isArray(response.DATA)) data = [response.DATA];
  else if (!Array.isArray(response)) data = [response];
  return data;
}

function getChildrenArray(node) {
  const info = getUserInfo(node);
  const kids = firstDefined(
    node.CHILDREN,
    node.children,
    node.Childrens,
    node.childrens,
    node.CLIENTS,
    node.clients,
    info.CHILDREN,
    info.children
  );
  return Array.isArray(kids) ? kids : [];
}

function flattenTree(list, depth = 0, out = []) {
  if (!Array.isArray(list)) return out;
  list.forEach((n) => {
    out.push({ node: n, depth });
    const children = getChildrenArray(n);
    if (children.length) flattenTree(children, depth + 1, out);
  });
  return out;
}

function parseClientStatus(client, info) {
  const profileStatus = firstDefined(
    client.PROFILE_STATUS,
    info.PROFILE_STATUS,
    client.profile_status,
    info.profile_status
  );
  if (!isMissing(profileStatus)) {
    const n =
      typeof profileStatus === "string" ? Number(profileStatus.trim()) : Number(profileStatus);
    if (!Number.isNaN(n)) {
      if (n === 1) return "completed";
      if (n === 0) return "incomplete";
    }
  }

  const s = firstDefined(
    client.USER_STATUS,
    info.USER_STATUS,
    client.STATUS,
    info.STATUS,
    client.IS_ACTIVE,
    info.IS_ACTIVE,
    client.user_status,
    client.ACTIVE
  );
  if (isMissing(s)) return null;
  if (typeof s === "number") {
    if (s === 1) return "active";
    if (s === 0) return "inactive";
  }
  const str = String(s).toLowerCase().trim();
  if (["1", "true", "active", "a", "enabled"].includes(str)) return "active";
  if (["0", "false", "inactive", "i", "disabled"].includes(str)) return "inactive";
  if (str.includes("suspend")) return "suspended";
  if (str.includes("pending") || str === "2") return "pending";
  return str || null;
}

function sliceSubtreeRows(flat, userId, options = {}) {
  const includeRoot = options.includeRoot !== false;
  if (isMissing(userId) || !Array.isArray(flat) || !flat.length) return flat;
  const uid = String(userId).trim();
  const idx = flat.findIndex((entry) => {
    const client = entry?.node ?? entry;
    const info = getUserInfo(client);
    const id = firstDefined(client.ID, client.USER_ID, client.UserId, client.id, info.USER_ID, info.ID);
    return id != null && String(id) === uid;
  });
  if (idx === -1) return [];
  const baseDepth = flat[idx].depth;
  const out = includeRoot ? [flat[idx]] : [];
  for (let i = idx + 1; i < flat.length; i++) {
    if (flat[i].depth <= baseDepth) break;
    out.push(flat[i]);
  }
  return out;
}

function formatClientDate(raw) {
  if (isMissing(raw)) return NAN;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return NAN;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function toFiniteNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatNetworkRevenueDisplay(networkRevenue) {
  if (!networkRevenue || typeof networkRevenue !== "object") return NAN;

  const valueRaw = firstDefined(networkRevenue.VALUE, networkRevenue.value);
  const numericValue = toFiniteNumber(valueRaw);
  if (numericValue === null) return NAN;

  const currencyRaw = firstDefined(
    networkRevenue.CURRENCY,
    networkRevenue.currency,
    networkRevenue.CURRENCY_CODE,
    networkRevenue.currency_code
  );
  const currency = isMissing(currencyRaw) ? "" : String(currencyRaw).trim();
  const formattedValue = numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return currency ? `${formattedValue} ${currency}` : formattedValue;
}

function normalizeTreeRow(entry, index) {
  const client = entry?.node ?? entry;
  const depth = entry?.depth ?? 0;
  const info = getUserInfo(client);

  const id = firstDefined(client.ID, client.USER_ID, client.UserId, client.id, info.USER_ID, info.ID);
  const rowKey = id != null && !isMissing(id) ? `id:${String(id)}` : `idx:${index}`;

  const nameRaw = firstDefined(
    client.NICK_NAME,
    client.NICK,
    info.NICK_NAME,
    info.FULL_NAME,
    client.FULL_NAME,
    client.NAME,
    info.NAME,
    client.username
  );
  const emailRaw =
    firstDefined(
      info.EMAIL,
      info.USER_EMAIL,
      client.EMAIL,
      client.USER_EMAIL,
      client.email,
      client.user_email,
      client.MAIL,
      info.MAIL
    ) ?? extractEmailLoose(client);
  const tradingIdRaw = firstDefined(
    client.REFERRAL_BROKER,
    info.REFERRAL_BROKER,
    client.TRADING_ID,
    info.TRADING_ID,
    client.TRADINGID,
    info.TRADINGID,
    client.TRADING_ID_VALUE,
    client.LOGIN,
    info.LOGIN,
    client.MT5_LOGIN,
    info.MT5_LOGIN
  );

  const joinAtRaw = firstDefined(
    client.CREATED_AT,
    client.DATE_CREATED,
    client.REGISTER_DATE,
    info.CREATED_AT,
    info.REGISTER_DATE,
    client.created_at
  );

  const directBrokerRaw = firstDefined(
    client.REFERRAL_BROKER_NAME,
    info.REFERRAL_BROKER_NAME,
    client.DIRECT_BROKER,
    client.BROKER_NAME,
    client.BROKER,
    info.BROKER_NAME,
    info.BROKER,
    client.DIRECT_BROKER_NAME
  );

  const subBrokerTeamRaw = firstDefined(
    client.SUB_BROKER,
    client.SUB_BROKER_TEAM,
    client.TEAM,
    client.TEAM_NAME,
    client.SUB_BROKER_NAME,
    info.SUB_BROKER_TEAM,
    info.TEAM_NAME
  );

  const statusKey = parseClientStatus(client, info);

  return {
    raw: client,
    depth,
    rowKey,
    id,
    nameRaw,
    emailRaw,
    tradingIdRaw,
    joinAtRaw,
    directBrokerRaw,
    subBrokerTeamRaw,
    statusKey,
  };
}

export default function IbClientsTree() {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  const isMobileLayout = useMediaMaxWidth(768);
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [nameQuery, setNameQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [scopedRootUserId, setScopedRootUserId] = useState(null);
  const [brokerNameFilter, setBrokerNameFilter] = useState(null);
  const [treePayload, setTreePayload] = useState(null);
  const searchWrapRef = useRef(null);
  const renderIbSkeleton = (className = "") => (
    <span className={`ib-dashboard-skeleton ${className}`.trim()} aria-hidden />
  );

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {}
  }, []);

  const { brokerPerm, brokerDevicePerm } = useMemo(() => getPermissionControllers(user), [user]);
  const canViewClientsTree = useMemo(
    () =>
      isBroker(user) &&
      !isAccountPermissionDisabled(
        brokerPerm,
        PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TREE_LIST
      ),
    [user, brokerPerm]
  );
  const canViewTreeContact = useMemo(
    () =>
      hasBrokerPermission(
        brokerPerm,
        brokerDevicePerm,
        PermissionBrokerModelController.PER__CAN_VIEW_TREE_CONTACT
      ) || hasBrokerPermission(brokerPerm, brokerDevicePerm, "CAN_VIEW_TREE_CONTACT"),
    [brokerPerm, brokerDevicePerm]
  );

  useEffect(() => {
    if (!user) return;
    if (!canViewClientsTree) {
      navigate("/pages/ibDashboard/dashboard", { replace: true });
    }
  }, [user, canViewClientsTree, navigate]);

  const treeTableClassName = useMemo(
    () =>
      `ib-clients-full-table ib-clients-tree-table${
        canViewTreeContact ? "" : " ib-clients-tree-table--compact"
      }`,
    [canViewTreeContact]
  );

  useEffect(() => {
    const loadTree = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAgentClients({
          page: 0,
          withDoc: 0,
          search: null,
          showTree: 1,
        });


        console.log("[IB Clients Tree] fetchAgentClients response:", response);
        setTreePayload(response || null);
        const data = extractClientsArray(response);
        const flatDebug = flattenTree(data, 0, []);
        console.log(
          "[IB Clients Tree] detected user types:",
          flatDebug.map((entry) => {
            const node = entry?.node ?? entry;
            const info = getUserInfo(node);
            const id = firstDefined(
              node?.ID,
              node?.USER_ID,
              node?.id,
              info?.ID,
              info?.USER_ID
            );
            const name = firstDefined(
              node?.NICK_NAME,
              node?.FULL_NAME,
              node?.NAME,
              info?.NICK_NAME,
              info?.FULL_NAME,
              info?.NAME
            );
            return {
              id: id ?? null,
              name: name ?? null,
              detectedType: inferUserType(node),
              rawType: firstDefined(
                node?.USER_TYPE,
                node?.user_type,
                node?.TYPE,
                node?.type,
                node?.ROLE,
                node?.role,
                node?.ACCOUNT_TYPE,
                node?.account_type,
                info?.USER_TYPE,
                info?.user_type,
                info?.TYPE,
                info?.type,
                info?.ROLE,
                info?.role
              ),
            };
          })
        );
        const flat = flattenTree(data, 0, []);
        setRows(flat);
      } catch (_) {
        setError("Failed to load IB client tree");
        setTreePayload(null);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    loadTree();
  }, []);

  const allNormalized = useMemo(
    () => (Array.isArray(rows) ? rows.map((r, i) => normalizeTreeRow(r, i)) : []),
    [rows]
  );

  const scopedRows = useMemo(() => {
    if (!isMissing(scopedRootUserId)) {
      // Keep the selected user visible, plus any descendants.
      const subtree = sliceSubtreeRows(rows, scopedRootUserId, { includeRoot: true });
      const selected = allNormalized.find(
        (row) => !isMissing(row.id) && String(row.id) === String(scopedRootUserId)
      );

      // For flat payloads, always include the selected user + rows whose direct broker
      // matches the selected name. This covers "search broker -> show broker and his clients"
      // even when backend user type is missing or misclassified.
      if (selected) {
        const selectedNameQuery = normalizeSearchValue(selected.nameRaw);
        if (selectedNameQuery) {
          const relatedRows = rows.filter((entry, index) => {
            const row = normalizeTreeRow(entry, index);
            if (!isMissing(row.id) && String(row.id) === String(scopedRootUserId)) return true;
            return normalizeSearchValue(row.directBrokerRaw).includes(selectedNameQuery);
          });
          if (relatedRows.length > 0) return relatedRows;
        }
      }

      if (subtree.length > 0) return subtree;
    }
    if (!isMissing(brokerNameFilter)) {
      const q = normalizeSearchValue(brokerNameFilter);
      if (q) {
        // Fallback for APIs that return flat rows where relation is only in direct broker field.
        return rows.filter((entry, index) => {
          const row = normalizeTreeRow(entry, index);
          return normalizeSearchValue(row.directBrokerRaw).includes(q);
        });
      }
    }
    const textQuery = normalizeSearchValue(nameQuery);
    if (textQuery) {
      // Free-text filtering should include both client name and direct broker columns.
      return rows.filter((entry, index) => {
        const row = normalizeTreeRow(entry, index);
        return (
          normalizeSearchValue(row.nameRaw).includes(textQuery) ||
          normalizeSearchValue(row.directBrokerRaw).includes(textQuery)
        );
      });
    }
    return rows;
  }, [rows, allNormalized, scopedRootUserId, brokerNameFilter, nameQuery]);

  const normalized = useMemo(
    () => (Array.isArray(scopedRows) ? scopedRows.map((r, i) => normalizeTreeRow(r, i)) : []),
    [scopedRows]
  );

  const nameSuggestions = useMemo(() => {
    const q = normalizeSearchValue(nameQuery);
    if (!q) return [];
    return allNormalized
      .filter(
        (row) =>
          !isMissing(row.id) &&
          (normalizeSearchValue(row.nameRaw).includes(q) ||
            normalizeSearchValue(row.directBrokerRaw).includes(q))
      )
      .slice(0, 12);
  }, [allNormalized, nameQuery]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!searchWrapRef.current || searchWrapRef.current.contains(e.target)) return;
      setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const applySearchByUserId = async (userId, displayName) => {
    if (isMissing(userId)) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const result = await fetchAgentClientAccounts(userId);
      const code = Number(result?.CODE ?? result?.code);
      if (code !== AppConstants.responseOk) {
        setSearchError(t("ib_tree_search_accounts_error"));
        return;
      }
      const inTree = sliceSubtreeRows(rows, userId).length > 0;
      if (!inTree) {
        setSearchError(t("ib_tree_search_not_in_tree"));
        return;
      }
      setScopedRootUserId(userId);
      setBrokerNameFilter(displayName != null ? String(displayName).trim() : null);
      setNameQuery(displayName != null ? String(displayName).trim() : "");
      setSearchOpen(false);
      setPage(1);
    } catch (_) {
      setSearchError(t("ib_tree_search_accounts_error"));
    } finally {
      setSearchLoading(false);
    }
  };

  const clearFilters = () => {
    setScopedRootUserId(null);
    setBrokerNameFilter(null);
    setNameQuery("");
    setSearchError(null);
    setSearchOpen(false);
  };

  const totalItems = normalized.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PER_PAGE) || 1);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return normalized.slice(start, start + PER_PAGE);
  }, [normalized, page]);

  useEffect(() => {
    setPage(1);
  }, [scopedRootUserId]);

  const showingFrom = totalItems === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const showingTo = totalItems === 0 ? 0 : Math.min(page * PER_PAGE, totalItems);

  const paginationSummary =
    !loading && !error ? (
      <p className="ib-clients-pagination-summary">
        {t("ib_pagination_showing", { from: showingFrom, to: showingTo, total: totalItems })}
      </p>
    ) : null;

  const statusClass = (statusKey) => {
    if (statusKey === "completed") return "ib-dashboard-status--active";
    if (statusKey === "incomplete") return "ib-dashboard-status--pending";
    if (statusKey === "active") return "ib-dashboard-status--active";
    if (statusKey === "pending") return "ib-dashboard-status--pending";
    if (statusKey === "suspended") return "ib-dashboard-status--suspended";
    return "ib-dashboard-status--inactive";
  };

  const statusLabel = (statusKey) => {
    if (statusKey === "completed") return t("completed", { defaultValue: "Completed" });
    if (statusKey === "incomplete") return t("incomplete", { defaultValue: "Incomplete" });
    if (statusKey === "active") return t("ib_status_active");
    if (statusKey === "pending") return t("ib_status_pending");
    if (statusKey === "suspended") return t("ib_status_suspended");
    if (statusKey === "inactive") return t("ib_status_inactive");
    return NAN;
  };

  const columns = [
    {
      key: "clientName",
      label: t("ib_col_client_name"),
      className: "ib-tree-col-name",
      render: (row) => (
        <div className="ib-dashboard-cell-user">
          <div className="ib-dashboard-cell-user-text" style={{ minWidth: 0 }}>
            <span
              className="ib-dashboard-name"
              style={{ paddingLeft: row.depth ? row.depth * 14 : 0 }}
              title={disp(row.nameRaw)}
            >
              {isMobileLayout ? disp(row.nameRaw) : formatIbClientName(row.nameRaw, 13, NAN)}
            </span>
            {canViewTreeContact && (
              <span className="ib-clients-id-email-sub">{disp(row.emailRaw)}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "tradingId",
      label: t("trading_id"),
      className: "ib-tree-col-trading-id",
      render: (row) => <span className="ib-clients-id-email-main">{disp(row.tradingIdRaw)}</span>,
    },
    {
      key: "directBroker",
      label: t("ib_col_direct_broker"),
      className: "ib-tree-col-direct-broker",
      render: (row) => <span>{disp(row.directBrokerRaw)}</span>,
    },
    {
      key: "joinAt",
      label: t("ib_col_join_at"),
      className: "ib-tree-col-join-at",
      render: (row) => <span>{formatClientDate(row.joinAtRaw)}</span>,
    },
    {
      key: "status",
      label: t("ib_col_status"),
      className: "ib-tree-col-status",
      render: (row) => (
        <span className={`ib-dashboard-status ${statusClass(row.statusKey)}`}>
          <span className="ib-dashboard-status-dot" aria-hidden />
          {statusLabel(row.statusKey)}
        </span>
      ),
    },
  ];

  const kpiTotalClients = !loading
    ? allNormalized.length.toLocaleString("en-US")
    : renderIbSkeleton("ib-dashboard-skeleton--kpi-value");
  const kpiSubBrokers = !loading
    ? (() => {
        const raw = firstDefined(
          treePayload?.ACTIVE_SUB_BROKERS,
          treePayload?.active_sub_brokers,
          treePayload?.DATA?.ACTIVE_SUB_BROKERS,
          treePayload?.DATA?.active_sub_brokers
        );
        const numeric = toFiniteNumber(raw);
        return numeric === null ? NAN : numeric.toLocaleString("en-US");
      })()
    : renderIbSkeleton("ib-dashboard-skeleton--kpi-value");
  const kpiRevenue = !loading
    ? formatNetworkRevenueDisplay(
        firstDefined(
          treePayload?.NETWORK_REVENUE,
          treePayload?.network_revenue,
          treePayload?.DATA?.NETWORK_REVENUE,
          treePayload?.DATA?.network_revenue
        )
      )
    : renderIbSkeleton("ib-dashboard-skeleton--kpi-value");

  return (
    <div className="ib-dashboard-page ib-clients-list-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main">
        <div className="container-fluid px-0 w-100">
        <h1 className="ib-clients-list-page-title">{t("ib_clients_tree_title")}</h1>

        <div className="ib-dashboard-kpi-grid" style={{ marginBottom: 24, gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          <article className="ib-dashboard-kpi-card">
            <div className="ib-dashboard-kpi-top">
              <div className="ib-dashboard-kpi-icon-wrap">
                <i className="fa-solid fa-users" />
              </div>
            </div>
            <p className="ib-dashboard-kpi-label">{t("ib_kpi_total_clients")}</p>
            <p className="ib-dashboard-kpi-value">{kpiTotalClients}</p>
          </article>

          <article className="ib-dashboard-kpi-card">
            <div className="ib-dashboard-kpi-top">
              <div className="ib-dashboard-kpi-icon-wrap">
                <i className="fa-solid fa-sitemap" />
              </div>
            </div>
            <p className="ib-dashboard-kpi-label">{t("ib_kpi_active_sub_brokers")}</p>
            <p className="ib-dashboard-kpi-value">{kpiSubBrokers}</p>
          </article>

          <article className="ib-dashboard-kpi-card">
            <div className="ib-dashboard-kpi-top">
              <div className="ib-dashboard-kpi-icon-wrap">
                <i className="fa-solid fa-sack-dollar" />
              </div>
            </div>
            <p className="ib-dashboard-kpi-label">{t("ib_kpi_network_revenue")}</p>
            <p className="ib-dashboard-kpi-value">{kpiRevenue}</p>
          </article>
        </div>

        <div className="ib-clients-filters-toolbar ib-clients-tree-filters-toolbar w-100">
          <div className="ib-clients-tree-toolbar-left">
            <div className="ib-clients-tree-search-block" ref={searchWrapRef}>
              <div className="ib-clients-filter-field ib-clients-tree-search-field">
                <label htmlFor="ib-tree-search-name">{t("ib_tree_search_by_name")}</label>
                <div className="ib-clients-tree-search-wrap">
                  <input
                    id="ib-tree-search-name"
                    type="search"
                    className="ib-clients-tree-search-input"
                    autoComplete="off"
                    placeholder={t("ib_tree_search_placeholder")}
                    value={nameQuery}
                    disabled={searchLoading || loading}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      // Free typing should search globally by client/direct broker name,
                      // so clear any previously scoped broker selection.
                      setScopedRootUserId(null);
                      setBrokerNameFilter(null);
                      setNameQuery(nextValue);
                      setSearchOpen(true);
                      setSearchError(null);
                    }}
                    onFocus={() => setSearchOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (nameSuggestions.length === 1) {
                          const one = nameSuggestions[0];
                          applySearchByUserId(one.id, one.nameRaw);
                        }
                      }
                      if (e.key === "Escape") setSearchOpen(false);
                    }}
                  />
                  {searchLoading ? (
                    <span className="ib-clients-tree-search-spinner" aria-hidden>
                      <i className="fa-solid fa-spinner fa-spin" />
                    </span>
                  ) : null}
                  {searchOpen && nameSuggestions.length > 0 ? (
                    <ul className="ib-clients-tree-search-suggestions">
                      {nameSuggestions.map((s) => (
                        <li key={s.rowKey}>
                          <button
                            type="button"
                            className="ib-clients-tree-search-suggestion-btn"
                            onMouseDown={(ev) => ev.preventDefault()}
                            onClick={() => applySearchByUserId(s.id, s.nameRaw)}
                          >
                            <span className="ib-clients-tree-search-suggestion-name">{disp(s.nameRaw)}</span>
                            <span className="ib-clients-tree-search-suggestion-id">{String(s.id)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
              {searchError ? <p className="ib-clients-tree-search-error">{searchError}</p> : null}
            </div>
            <button
              type="button"
              className="ib-clients-filter-clear ib-clients-tree-filter-clear"
              onClick={clearFilters}
            >
              <i className="fa-solid fa-filter-circle-xmark" aria-hidden />
              {t("ib_filter_clear")}
            </button>
          </div>
          <div className="ib-clients-bulk ib-clients-tree-bulk" aria-label={t("ib_bulk_actions")}>
            <span>{t("ib_bulk_actions")}</span>
            <button type="button" className="ib-clients-bulk-btn" aria-label="Email">
              <i className="fa-regular fa-envelope" />
            </button>
            <button type="button" className="ib-clients-bulk-btn" aria-label="Download">
              <i className="fa-solid fa-download" />
            </button>
          </div>
        </div>

        <DataTable
          title=""
          columns={columns}
          data={paginated}
          rowKey={(row) => row.rowKey}
          currentPage={page}
          totalItems={totalItems}
          perPage={PER_PAGE}
          onPageChange={setPage}
          hideTopToolbar
          loading={loading}
          loadingMessage={t("ib_clients_loading")}
          error={error}
          noDataMessage={t("ib_clients_no_results")}
          containerClassName="container-fluid px-0 w-100 ib-clients-list-container ib-clients-tree-container"
          tableClassName={treeTableClassName}
          paginationLeading={paginationSummary}
        />
        </div>
      </div>
    </div>
  );
}

